import { BLOCKS, MARKS, Document } from "@contentful/rich-text-types";
import { documentToHtmlString, Options } from "@contentful/rich-text-html-renderer";
import { html, TemplateResult } from "lit";
import { unsafeHTML } from "lit-html/directives/unsafe-html.js";

// Options for rendering Contentful rich text
const getOptions = (className?: string): Options => {
	const options: Options = {
		renderMark: {
			[MARKS.BOLD]: (text: string) => `<strong>${text}</strong>`,
			[MARKS.ITALIC]: (text: string) => `<em>${text}</em>`,
		},
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node, next) =>
				`<contentful-paragraph class="${className}">${next(node.content)}</contentful-paragraph>`,
			[BLOCKS.UL_LIST]: (node, next) =>
				`<ul style="list-style-type: disc; list-style-position: inside;">${next(node.content)}</ul>`,
			[BLOCKS.OL_LIST]: (node, next) =>
				`<ol style="list-style-type: decimal; list-style-position: inside;">${next(node.content)}</ol>`,
			// Contentful renders a p tag inside li, this makes sure the text is directly inside li
			[BLOCKS.LIST_ITEM]: (node) => {
				const transformedChildren = documentToHtmlString(node as Document, {
					renderNode: {
						[BLOCKS.PARAGRAPH]: (node, next) => `${next(node.content)}`,
						[BLOCKS.LIST_ITEM]: (node, next) => `${next(node.content)}`,
					},
				});

				return `<li>${transformedChildren}</li>`;
			},
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				// Contentful stores asset file under fields.file and it may be localized
				const file = node?.data?.target?.fields?.file;
				const title = node?.data?.target?.fields?.title || "";
				if (!file) return "";

				// handle localization: pick the first key (or pick specific locale)
				const src = file?.url ? (file.url.startsWith("//") ? `https:${file.url}` : file.url) : "";
				const width = file?.details?.image?.width;
				const height = file?.details?.image?.height;

				return `<img src="${src}" alt="${escapeHtml(title)}" ${width ? `width="${width}"` : ""} ${height ? `height="${height}"` : ""} />`;
			},
		},
	};

	return options;
};

function escapeHtml(s = "") {
	return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
}

// Function to render rich text
export function renderRichText(document: Document, className?: string): TemplateResult {
	const htmlString = documentToHtmlString(document, getOptions(className));
	return html`${unsafeHTML(htmlString)}`;
}
